import type { CSSProperties } from 'react';
import { documentMathTextClassName } from '../theme/contentTypography';
import {
  renderContentBodyHtml,
  renderDocumentPlainBodyHtml,
  renderDocumentPlainInlineHtml,
  renderInlineContentBodyHtml,
  shouldRenderRichTextBodyHtml,
} from '../utils/renderRichTextHtml';
import {
  expandJsonNewlineEscapes,
  repairCorruptedTimesInMathText,
  repairLatexEscapes,
  splitAdjacentInlineMathDelimiters,
  wrapBareArithmeticCommands,
  wrapBareLatexEnvironments,
  wrapBareScriptAtoms,
  wrapBareStructCommands,
} from '../utils/repairLatexEscapes';

type MathTextBlockProps = {
  text: string;
  className?: string;
  style?: CSSProperties;
  /** 卡族/文书内容：小四宋体 + 罗马体 */
  variant?: 'default' | 'document';
  /** 内联进爆炸句：不打断自然文气流 */
  inline?: boolean;
};

/** 与 Mobile2 MathTextBlock CONTROL_CHAR_LATEX_REPAIRS 一致 */
const CONTROL_CHAR_LATEX_REPAIRS: Array<[RegExp, string]> = [
  [/\u000crac/g, '\\frac'],
  [/\u0008egin/g, '\\begin'],
  [/\u0008inom/g, '\\binom'],
  [/\u0008oxed/g, '\\boxed'],
  [/\u000dule/g, '\\rule'],
  [/\u000bphantom/g, '\\vphantom'],
  [/\u0009ext/g, '\\text'],
  [/\u0009imes/g, '\\times'],
  [/\u0009heta/g, '\\theta'],
  [/\u0009riangle/g, '\\triangle'],
  [/\u0009an/g, '\\tan'],
  [/\u0009o/g, '\\to'],
  [/\u000dight/g, '\\right'],
];

const BARE_LATEX_COMMAND_PATTERN = /\\(?:binom|frac|sqrt|sum|int|prod|lim|alpha|beta|gamma|delta|theta|pi|sigma|mu|infty|cdot|times|leq|geq|neq|approx|in|notin|subset|cup|cap|left|right|begin|end|mathbb|mathbf|mathrm|overline|underline|vec|hat|bar|dot|text|boxed|phantom|vphantom|hspace|rule|fcolorbox|fbox|triangle|tan|to|bigl|bigr|leq|ge)\b/;

function repairBrokenLatexEscapes(text: string) {
  return CONTROL_CHAR_LATEX_REPAIRS.reduce(
    (current, [pattern, replacement]) => current.replace(pattern, replacement),
    text,
  );
}

function wrapBareLatexMath(text: string) {
  // 无论是否已有部分 $…$，都补包裸 \\binom / \\frac / \\times / 下标等，避免混排泄露源码
  let source = wrapBareArithmeticCommands(
    wrapBareScriptAtoms(wrapBareStructCommands(String(text || ''))),
  );

  // Truncated previews may leave a dangling `$` — drop it so bare \\times can be re-wrapped.
  const dollarCount = (source.match(/(?<!\\)\$/g) || []).length;
  if (dollarCount === 1) {
    source = source.replace(/(?<!\\)\$/g, '');
  }

  // 多行环境必须走 $$…$$；否则会在行内 $ 中失败并露出红色源码
  source = wrapBareLatexEnvironments(source);

  if (/(\$\$|\$|\\\(|\\\[)/.test(source)) {
    return wrapBareTimesFormula(source);
  }

  const looksLikeBareLatex =
    BARE_LATEX_COMMAND_PATTERN.test(source)
    || /[_^]\{[^}]+\}/.test(source)
    || /\\div\b/.test(source);

  if (looksLikeBareLatex && !source.includes('\n')) {
    return `$${source.replace(/^\$+|\$+$/g, '').trim()}$`;
  }

  return wrapBareTimesFormula(source);
}

/** 无 $ 定界符的 `N=k\times(…)+1` — repairCorruptedTimesInMathText 已处理，此处仅兜底 */
function wrapBareTimesFormula(text: string): string {
  return repairCorruptedTimesInMathText(text);
}

/** Mobile2 MathTextBlock.normalizeLatexText 同源 + Web 端 times 残片修复 */
function normalizeLatexText(input: string) {
  const cleaned = repairBrokenLatexEscapes(String(input || ''));
  const timesFixed = repairCorruptedTimesInMathText(cleaned);
  const withNewlines = expandJsonNewlineEscapes(timesFixed)
    .replace(/\u0000/g, '')
    .replace(/\u000b/g, ' ')
    .replace(/\r\n/g, '\n')
    .replace(/\\\((.+?)\\\)/gs, (_, c) => `$${c}$`)
    .replace(/\\\[(.+?)\\\]/gs, (_, c) => `$$${c}$$`);

  const repaired = repairLatexEscapes(splitAdjacentInlineMathDelimiters(withNewlines));
  return wrapBareLatexMath(repaired).trim();
}

export function MathTextBlock({
  text,
  className = '',
  style,
  variant = 'default',
  inline = false,
}: MathTextBlockProps) {
  const normalized = normalizeLatexText(String(text || ''));
  if (!normalized) {
    return null;
  }

  const variantClass = variant === 'document' ? documentMathTextClassName : '';
  const useRich = shouldRenderRichTextBodyHtml(normalized)
    || /\$[^$\n]+\$/.test(normalized)
    || /\$\$[\s\S]+?\$\$/.test(normalized)
    || /\\\(|\\\[/.test(normalized)
    || BARE_LATEX_COMMAND_PATTERN.test(normalized);

  const html = useRich
    ? (inline ? renderInlineContentBodyHtml(normalized) : renderContentBodyHtml(normalized))
    : (inline ? renderDocumentPlainInlineHtml(normalized) : renderDocumentPlainBodyHtml(normalized));

  if (inline) {
    return (
      <span
        className={`document-math-text-inline text-inherit [&_.katex]:text-inherit ${variantClass} ${className}`.trim()}
        style={style}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  return (
    <div
      className={
        (
          `prose prose-sm max-w-none overflow-x-auto text-inherit `
          + `[&_.katex]:text-inherit [&_.katex-display]:my-2 `
          + `${variantClass} ${className}`
        ).trim()
      }
      style={style}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export function DocumentMathTextBlock(props: Omit<MathTextBlockProps, 'variant'>) {
  return <MathTextBlock {...props} variant="document" />;
}
